import { authedApiFetch } from 'utils/Api';

const salesforceTasksService = {
	getTasks: async (limit = 50) => {
		const response = await authedApiFetch({
			endpoint: '/salesforce_tasks',
			method: 'GET',
			params: { limit },
		});

		return response;
	},

	// Fetch paginated and sorted tasks for the Work Queue
	getWorkQueueTasks: async (
		limit = 25,
		cursor = null,
		filter = 'open',
		sortBy = 'priority',
		sortDirection = 'asc'
	) => {
		return authedApiFetch({
			endpoint: '/salesforce_tasks/work_queue',
			method: 'GET',
			params: {
				filter,
				limit,
				sort_by: sortBy,
				sort_direction: sortDirection,
				...(cursor ? { cursor } : {}),
			},
		});
	},

	getFormOptions: async () => {
		return authedApiFetch({
			endpoint: '/salesforce_tasks/form_options',
			method: 'GET',
		});
	},

	// Generate a task-focused subject from the alert associated with the input signal
	suggestSubject: async (inputSignalId) => {
		return authedApiFetch({
			endpoint: '/salesforce_tasks/suggest_subject',
			method: 'POST',
			payload: {
				input_signal_id: inputSignalId,
			},
		});
	},

	// Relate the task to an alert's record (input_signal_id) or directly to a
	// Salesforce record (what_id)
	createTask: async ({
		input_signal_id,
		what_id,
		subject,
		owner_id,
		status,
		priority,
		due_date,
		comments,
		convert_to_interaction,
	}) => {
		const response = await authedApiFetch({
			endpoint: '/salesforce_tasks',
			method: 'POST',
			payload: {
				input_signal_id,
				what_id,
				subject,
				owner_id,
				status,
				priority,
				due_date,
				comments,
				convert_to_interaction,
			},
			returnResponse: true,
		});

		if (!response.ok) {
			const body = await response.json();

			const message =
				getSalesforceErrorMessage(body?.error) ||
				'Unable to create Salesforce task.';

			const error = new Error(message);

			error.status = response.status;
			error.code = body?.error?.[0]?.errorCode;

			throw error;
		}

		return response.json();
	},

	// Apply one field update to specific tasks, chunking into groups of 200 to
	// respect the Salesforce sObject Collections limit
	bulkUpdateTasks: async ({ taskIds, field, value }) => {
		const aggregated = {
			success: true,
			updated_count: 0,
			failed_ids: [],
			results: [],
		};

		for (
			let index = 0;
			index < taskIds.length;
			index += BULK_UPDATE_CHUNK_SIZE
		) {
			const result = await bulkUpdateRequest({
				task_ids: taskIds.slice(index, index + BULK_UPDATE_CHUNK_SIZE),
				field,
				value,
			});

			aggregated.updated_count += result.updated_count ?? 0;
			aggregated.results.push(...(result.results ?? []));
		}

		const failedRecords = aggregated.results.filter(
			(record) => !record.success
		);

		aggregated.failed_ids = failedRecords.map((record) => record.id);
		aggregated.success = aggregated.failed_ids.length === 0;

		return aggregated;
	},

	updateTask: async (taskId, updates) => {
		const response = await authedApiFetch({
			endpoint: `/salesforce_tasks/${taskId}`,
			method: 'PATCH',
			payload: {
				task: updates,
			},
			returnResponse: true,
		});

		if (!response.ok) {
			const body = await response.json();

			const message =
				getSalesforceErrorMessage(body?.error) ||
				'Unable to update Salesforce task.';

			const error = new Error(message);

			error.status = response.status;
			error.code = body?.error?.[0]?.errorCode;

			throw error;
		}

		return response.json();
	},
};

export default salesforceTasksService;

const BULK_UPDATE_CHUNK_SIZE = 200;

const bulkUpdateRequest = async (payload) => {
	const response = await authedApiFetch({
		endpoint: '/salesforce_tasks/bulk_update',
		method: 'PATCH',
		payload,
		returnResponse: true,
	});

	if (!response.ok) {
		const body = await response.json();

		const message =
			getSalesforceErrorMessage(body?.error) ||
			'Unable to update Salesforce tasks.';

		const error = new Error(message);

		error.status = response.status;
		error.code = body?.error?.[0]?.errorCode;

		throw error;
	}

	return response.json();
};

// Normalize Salesforce API errors into user-friendly messages, providing
// specific guidance for known validation errors and falling back to the
// available backend message when no special handling is required
const getSalesforceErrorMessage = (error) => {
	if (typeof error === 'string') {
		return error;
	}

	if (Array.isArray(error)) {
		const firstError = error[0];

		if (
			firstError?.errorCode === 'MALFORMED_ID' &&
			firstError?.fields?.includes('OwnerId')
		) {
			return 'The selected user cannot be assigned tasks in Salesforce. Please choose another user.';
		}

		if (
			firstError?.errorCode === 'MALFORMED_ID' &&
			firstError?.fields?.includes('WhatId')
		) {
			return 'The related record is not valid in CRM. Please verify it is linked to a valid CRM record.';
		}

		const messages = error
			.map((item) => (typeof item === 'string' ? item : item?.message))
			.filter(Boolean);

		return messages.join(', ') || null;
	}

	if (error?.message) {
		return error.message;
	}

	return null;
};
